import {
  type MutationFunction,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import Api, { withAcl } from 'hooks/Api';
import { toastSuccess } from 'ui/partials/Toast';
import { removeEmpty, useAdminParams } from 'utils/helpers';
import { useProvider } from 'utils/providers/Provider';
import useModal from 'ui/partials/Modal/useModal';
import { ApiError, Company, Meta } from 'utils/types';
import { useTour } from '@reactour/tour';
import partialConstants from 'ui/partials/utils/constants';

import { COMPANY_FORM } from '../forms/schema';
import c from '../utils/constants';

interface Result {
  data: Company;
  meta: Meta;
}

export default function useCreateCompany(disableHide = false) {
  const queryClient = useQueryClient();
  const { hideModal } = useModal();
  const { state } = useProvider();
  const { isOpen, setIsOpen } = useTour();
  const args = state;
  const { accountId, accountUserId } = useAdminParams();

  const mutationFn: MutationFunction<any, COMPANY_FORM> = async (payload) => {
    // Triggers a successful mutation without writing to the DB
    // Only used when a tour is opened
    if (isOpen) return setIsOpen(false);
    const api = new Api(args);

    const data = await api.post(
      await withAcl({
        account: {
          url: 'api/v1/contacts/companies',
          payload: removeEmpty(payload),
        },
        admin: {
          url: `api/v2/accounts/${accountId}/users/${accountUserId}/contacts/companies`,
          payload: removeEmpty(payload),
        },
      }),
    );

    return data;
  };

  return useMutation<Result, ApiError, any, any>({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [c.COMPANIES, {}] });
      queryClient.invalidateQueries({
        queryKey: [partialConstants.TAGS],
      });

      if (!disableHide) hideModal();

      toastSuccess('Successfully created company');
    },
  });
}
